/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react';
import { IconButton } from '@fluentui/react/lib/Button';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { QnaMakerQnaActiveLearningSuggestion } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

export type QnaMakerReviewSuggestionsQuestionColumnRendererProps = {
    questions: string[];
    isInEditMode?: boolean;
    isInAddMode?: boolean;
    isActiveLearningModeEnabled?: boolean;
    activeLearningSuggestions: QnaMakerQnaActiveLearningSuggestion[];
    onEditQuestions: (
        questions: string[],
        activeLearningSuggestions: QnaMakerQnaActiveLearningSuggestion[],
        isSuggestionsAccepted: boolean
    ) => void;
    onChangeAlternativeQuestionSelection: (suggestion: string, isSuggestionsSelected: boolean) => void;
    areActionsDisabled: boolean;
    selectedSuggestions: string[];
};

const messages = defineMessages({
    acceptSuggestion: {
        id: 'qnaMaker.QnaMakerReviewSuggestionsQuestionColumnRenderer.acceptSuggestion',
        defaultMessage: 'Accept suggestion'
    },
    rejectSuggestion: {
        id: 'qnaMaker.QnaMakerReviewSuggestionsQuestionColumnRenderer.rejectSuggestion',
        defaultMessage: 'Reject suggestion'
    },
    selectSuggestion: {
        id: 'qnaMaker.QnaMakerReviewSuggestionsQuestionColumnRenderer.selectSuggestion',
        defaultMessage: 'Select suggestion'
    }
});

const { styleComponent } = getStylistV2('QnaMakerQuestionsColumnRenderer');
const SuggestedQuestionItem = styleComponent<IStackProps>(Stack)('SuggestedQuestionItem', theme => ({
    width: '80%',
    border: `1px solid ${theme.actionBtnColor}`,
    paddingLeft: '5px',
    paddingRight: '5px',
    borderRadius: '5px',
    backgroundColor: theme.suggestedQuestionBackgroundColor
}));

/**
 * A component rendering the questions column in the review suggestions page QnAs.
 */
export const QnaMakerReviewSuggestionsQuestionColumnRenderer = observer((props: QnaMakerReviewSuggestionsQuestionColumnRendererProps) => {
    const {
        questions,
        areActionsDisabled,
        activeLearningSuggestions,
        selectedSuggestions,
        onEditQuestions,
        onChangeAlternativeQuestionSelection
    } = props;
    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();

    const { isSmartSuggestionEnabled } = rootStore.currentQnaMakerProjectStore;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const { languageDirection } = rootStore.currentQnaMakerProjectStore;

    const activeQuestionIdRef = React.useRef<string>(getId());

    React.useEffect(() => () => dequeueTeachingBubble('QnaActiveReviewSuggestion'), []);

    React.useEffect(() => {
        if (canWrite && !areActionsDisabled && !uiStore.teachingBubbles.QnaActiveReviewSuggestion) {
            createTeachingBubble(
                'QnaActiveReviewSuggestion',
                localize(qnaMakerTeachingBubbleMessages.activeReviewSuggestionBody),
                localize(qnaMakerTeachingBubbleMessages.activeReviewSuggestionTitle),
                `#${activeQuestionIdRef.current}`,
                true
            );
        }
    }, [canWrite, areActionsDisabled, Object.values(uiStore.teachingBubbles)]);

    const onAcceptSuggestion = (suggestion: string) => {
        const filteredSuggestions = activeLearningSuggestions.filter(s => s.clusterHead !== suggestion);
        onEditQuestions([...questions, suggestion], filteredSuggestions, true);
    };

    const onRejectSuggestion = (suggestion: string) => {
        const filteredSuggestions = activeLearningSuggestions.filter(s => s.clusterHead !== suggestion);
        onEditQuestions(questions, filteredSuggestions, false);
    };

    return (
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { width: '100%' } }} verticalFill>
            {questions.map(question => (
                <TruncatedText
                    styles={isSmartSuggestionEnabled && { root: { marginLeft: '45px' } }}
                    className="ck"
                    dir={languageDirection}
                    key={question}
                    maxWidth="90%"
                    title={question}>
                    {question}
                </TruncatedText>
            ))}

            {isSmartSuggestionEnabled
                ? activeLearningSuggestions.map(({ clusterHead }) => (
                      <Stack dir={languageDirection} horizontal verticalAlign="center">
                          <Checkbox
                              ariaLabel={localize(messages.selectSuggestion)}
                              styles={{ checkbox: { borderRadius: '10px' } }}
                              checked={!!selectedSuggestions.find(s => s === clusterHead)}
                              onChange={(_, checked: boolean) => onChangeAlternativeQuestionSelection(clusterHead, checked)}
                          />

                          <TruncatedText
                              styles={{ root: { paddingLeft: '21px' } }}
                              maxWidth="890%"
                              key={clusterHead}
                              title={clusterHead}
                              fontWeight={600}
                              id={activeQuestionIdRef.current}>
                              {clusterHead}
                          </TruncatedText>
                      </Stack>
                  ))
                : activeLearningSuggestions.map(({ clusterHead }) => (
                      <SuggestedQuestionItem dir={languageDirection} horizontal horizontalAlign="space-between" verticalAlign="center">
                          <TruncatedText maxWidth="80%" key={clusterHead} title={clusterHead} id={activeQuestionIdRef.current}>
                              {clusterHead}
                          </TruncatedText>

                          <Stack.Item>
                              <RBACTooltip
                                  displayedUserRole={displayedUserRole}
                                  hidden={canWrite || areActionsDisabled}
                                  data-automation-id="checkMarkButtonLanguageToolTip"
                                  componentSurroundedByToolTip={
                                      <IconButton
                                          iconProps={{ iconName: 'CheckMark' }}
                                          disabled={!canWrite || areActionsDisabled}
                                          onClick={() => onAcceptSuggestion(clusterHead)}
                                          aria-label={localize(messages.acceptSuggestion)}
                                      />
                                  }
                              />

                              <RBACTooltip
                                  displayedUserRole={displayedUserRole}
                                  hidden={canWrite || areActionsDisabled}
                                  data-automation-id="deleteButtonLanguageToolTip"
                                  componentSurroundedByToolTip={
                                      <IconButton
                                          iconProps={{ iconName: 'Delete' }}
                                          disabled={!canWrite || areActionsDisabled}
                                          onClick={() => onRejectSuggestion(clusterHead)}
                                          aria-label={localize(messages.rejectSuggestion)}
                                      />
                                  }
                              />
                          </Stack.Item>
                      </SuggestedQuestionItem>
                  ))}
        </Stack>
    );
});
